// common
import "./styles.css"
import React, { useEffect, useState } from "react"

// redux
import { connect } from "react-redux"
import { mapDispatchProps, mapStateToProps } from "../../store/store"

// components
import Label from "../label"
import clsx from "clsx"

import { X } from "react-feather"
import { useNavigate } from "react-router-dom"
import { ROUTES } from "../../routes"
import Button from "../button"
import BRAND from "../../constants/brand"
import { getLanguage, setLanguage, useLanguage } from "@languages/index"
import { LOCALES } from "constants/locales"
import API from "api"
import { isEmpty } from "helpers/object"
import { isKyolook } from "constants/domains/domains"

// Props Interface
interface Props {
  className?: any
  style?: any

  sidebar?: any
  preferences?: any
  brand?: any
  auth?: any

  setSidebar?: (params?: any) => void
  setPreferences?: (params?: any) => void
  setSettings?: (params?: any) => void
  showModal?: (params?: any) => void
  showSheet?: (params?: any) => void

  showOverlays?: (params?: any) => void
  closeOverlays?: () => void

  setUser?: (params?: any) => void
  setAuth?: (params?: any) => void
  setBrand?: (params?: any) => void
  logout?: () => void
}

const SidebarContainer = (props: Props) => {
  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.component.sidebar }

  const isWholesale = props.brand.businessType === BRAND.BUSINESS_TYPE.WHOLESALE
  const isNotAvailableInJapan = process.env.LOCALE == LOCALES.JA
  const isLectureOnlyAccount = BRAND.isLectureOnlyBrand(props.brand.id)
  const isBeautyCategoryBrand = props.brand?.isCategoryBeauty
  const isMaster = props.auth?.isMaster

  const beautyIntroPageUrl =
    process.env.LOCALE == LOCALES.JA
      ? `https://glovv.co.kr/japan`
      : `https://glovv.co.kr/?isVisibleContactForm=0`

  const [brandMenus, setBrandMenus] = useState([])
  const [accountMenus, setAccountMenus] = useState([])
  const [languageMenus, setLanguageMenus] = useState([])

  useEffect(() => {
    setBrandMenus([
      {
        hidden:
          isWholesale ||
          isLectureOnlyAccount ||
          props.brand.hasExperiencedInfoUpdate ||
          props.brand.hasExperiencedAlarmUpdate ||
          props.brand.hasExperiencedPasswordUpdate,
        name: languageTexts.is_first_visit,
        url: `/help/missions`,
        isNew: false,
      },
      {
        name: languageTexts.brand_info,
        url: `/settings/brand`,
        isNew: false,
        hidden: isLectureOnlyAccount,
        children: [
          {
            name: languageTexts.setting_brand,
            url: "/settings/brand",
            hidden: isWholesale,
          },
          { name: languageTexts.setting_alarm, url: "/settings/brand/alarm" },
          {
            name: languageTexts.change_password,
            url: "/settings/brand/password",
          },
        ],
      },
      {
        hidden: isWholesale || isNotAvailableInJapan || isBeautyCategoryBrand || isLectureOnlyAccount,
        name: languageTexts.rent_product,
        url: ROUTES.RENTAL_INDEX.PATH,
        children: isLectureOnlyAccount
          ? undefined
          : [
              // { name: languageTexts.introduce, url: ROUTES.RENTAL_INTRO.PATH },
              { name: languageTexts.create_n_manage, url: ROUTES.RENTAL_INDEX.PATH },
            ],
      },
      {
        hidden: isLectureOnlyAccount,
        name: languageTexts.product_presentation,
        url: ROUTES.CAMPAIGN_INTRO.PATH,
        children: isLectureOnlyAccount
          ? null
          : [
              {
                name: languageTexts.introduce,
                url: isBeautyCategoryBrand ? beautyIntroPageUrl : ROUTES.CAMPAIGN_INTRO.PATH,
              },
              {
                name: languageTexts.create_n_manage,
                url: ROUTES.CAMPAIGN_INDEX.PATH,
              },
              {
                name: languageTexts.payment_history1,
                url: ROUTES.CAMPAIGN_PAYMENTS_INDEX.PATH,
              },
            ],
      },
      {
        hidden: isWholesale || isNotAvailableInJapan || isBeautyCategoryBrand || isLectureOnlyAccount,
        isBest: true,
        name: languageTexts.connect_company_mall,
        url: ROUTES.SNS_INTRO.PATH,
        children: props.brand.remainSnsPass
          ? [
              {
                name: languageTexts.introduce,
                url: ROUTES.SNS_INTRO.PATH,
              },
              {
                name: languageTexts.connect_n_manage,
                url: ROUTES.SNS_INDEX.PATH,
                hidden: isLectureOnlyAccount,
              },
              {
                name: languageTexts.payment_history1,
                url: ROUTES.SNS_PAYMENTS_INDEX.PATH,
                hidden: isLectureOnlyAccount,
              },
            ]
          : undefined,
      },
      {
        hidden: isWholesale || isLectureOnlyAccount || isBeautyCategoryBrand,
        // isBest: true,
        name: languageTexts.invite_offline,
        url: ROUTES.INVITE_INTRO.PATH,
      },
      {
        name: languageTexts.posting,
        url: "/postings",
        hidden: isLectureOnlyAccount,
      },
      // {
      //   hidden: isWholesale,
      //   name: languageTexts.report,
      //   url: `/reports`,
      // },
      {
        isBest: true,
        hidden: isNotAvailableInJapan || isLectureOnlyAccount,
        name: languageTexts.japan,
        url: ROUTES.JAPAN.PATH,
      },
      {
        hidden: isNotAvailableInJapan,
        name: languageTexts.lectures,
        url: ROUTES.LECTURE_INDEX.PATH,
      },
    ])

    setAccountMenus([
      {
        name: languageTexts.manage_card,
        url: "/cards",
        hidden: isLectureOnlyAccount,
      },
      isBeautyCategoryBrand ? {
        name: languageTexts.payment_history1,
        url: ROUTES.CAMPAIGN_PAYMENTS_INDEX.PATH,
        hidden: isLectureOnlyAccount,
      } : {
        name: languageTexts.payment_history1,
        url: "/payments",
        hidden: isLectureOnlyAccount,
      },
      {
        name: languageTexts.logout,
        url: `/logout`,
      },
    ])

    setLanguageMenus([
      {
        hidden: null,
        name: languageTexts.language,
        children: [
          {
            name: languageTexts.locale_korea,
            hidden: null,
            locale: LOCALES.KO,
            icon: <img className="w-[16px] h-[16px]" src={"/assets/icons/flags-ko.svg"} />,
          },
          {
            name: languageTexts.locale_japan,
            hidden: null,
            locale: LOCALES.JA,
            icon: <img className="w-[16px] h-[16px]" src={"/assets/icons/flags-ja.svg"} />,
          },
        ],
      },
    ])
  }, [props.preferences.locale, props.brand, props.auth])

  const navigate = useNavigate()
  const [selected, setSelected] = useState<any>()
  const fadeOutDuration = 300

  useEffect(() => {
    if (props.sidebar.closeModal) {
      setTimeout(() => {
        props.setSidebar({ isVisible: false })
      }, fadeOutDuration)
    }
  }, [props.sidebar])

  const closeSidebar = () => {
    props.setSidebar({ isVisible: false })
  }

  const handleTabClick = (tab: any) => {
    if (tab.url.startsWith("/products") && props.preferences.isMobile) {
      props.showOverlays({
        content: () => {
          return (
            <div className="flex flex-col justify-center bg-white w-[100dvw] px-5 py-10 gap-10">
              <span className="font-bold tracking-tighter text-xl text-center">{languageTexts._alertUsePc1()}</span>
              <Button
                text={languageTexts.confirm}
                buttonColor={"black"}
                textColor="white"
                textClassName={"text-white tracking-tighter text-sm font-medium"}
                onClick={() => {
                  props.closeOverlays()
                }}
              />
            </div>
          )
        },
      })
      return false
    }

    if (!isWholesale && tab.url.startsWith("/campaigns") && props.preferences.isMobile) {
      props.showSheet({
        content: () => {
          return (
            <div className="flex flex-col justify-center bg-white w-[100dvw] px-5 py-10 gap-10">
              <span className="font-bold tracking-tighter text-xl text-center">{languageTexts._alertUsePc1()}</span>
              <Button
                text={languageTexts.confirm}
                buttonColor={"black"}
                textColor="white"
                textClassName={"text-white tracking-tighter text-sm font-medium"}
                onClick={() => {
                  props.closeOverlays()
                }}
              />
            </div>
          )
        },
      })
      return false
    }

    if (tab.url == "/logout") {
      props.logout()
      props.setSidebar({ isVisible: false })
      navigate("/")
      return false
    }

    setSelected(tab)

    if (tab.children && tab.children.length > 1 && tab.children.filter((child: any) => !child.hidden).length > 1) {
      return false
    }

    if (
      tab.url.startsWith("https://oneulook.com") ||
      tab.url.startsWith("https://kyolook.com") ||
      tab.url.startsWith("https://glovv.co.kr")
    ) {
      window.open(tab.url, "_blank")
      return false
    }

    navigate(tab.url)
    closeSidebar()
  }

  // @todo header, rootEventListener, sidebar. 필요하면 통합
  const getBrandCurrencyText = () => {
    const serverLocale = process.env.LOCALE
    const userLocale = getLanguage() ?? props?.preferences?.locale
    const isJpy = props.brand.currency === "JPY"
    const isKrw = props.brand.currency === "KRW"
    const provider = isJpy ? "JPY" : "KRW"

    const currencyMap = {
      JPY: {
        ko: "엔",
        ja: "円",
        default: { ko: "엔", ja: "円" },
      },
      KRW: {
        ko: "원",
        ja: "ウォン",
        default: { ko: "원", ja: "ウォン" },
      },
    }

    if (isJpy || isKrw) {
      return currencyMap[provider][userLocale] || currencyMap[provider].default[serverLocale]
    }

    return currencyMap[provider].default[serverLocale] || currencyMap[provider].default[props.preferences.locale]
  }

  const handleLanguageClick = async (nextLocale: any) => {
    props.setPreferences({ locale: nextLocale })
    props.setSettings({ currencyText: getBrandCurrencyText() })
    setLanguage(nextLocale)
    const url = window.location.pathname
    navigate("/-1", { replace: true })

    const response = await API.Brand.update({
      brandToken: props.auth.token,
      locale: nextLocale,
    })

    setTimeout(() => {
      navigate(url, { replace: true })
    }, 300)
  }

  const renderSideBar = () => {
    return (
      <>
        <div className="dimmer animate__animated animate__fadeIn" onClick={closeSidebar} />

        <div className="sidebar w-[100vw] md:max-w-[375px] flex flex-col fixed left-0 top-0 bottom-0 bg-white animate__animated animate__slideInLeft">
          <div className="flex flex-row mt-1">
            <div className="ml-auto p-4 cursor-pointer" onClick={closeSidebar}>
              <X size={24} />
            </div>
          </div>
          <div className="flex flex-col p-5 gap-8">
            {brandMenus.map((menu, i) => {
              if (menu.hidden) return null

              const isActive = menu.url.endsWith(location.pathname) ? "active" : null

              return (
                <div
                  key={i}
                  className="flex flex-col point-cursor"
                  //   onPointerEnter={() => {
                  //     setSelected(menu);
                  //   }}
                  //   onPointerLeave={() => {
                  //     setSelected(null);
                  //   }}
                  onClick={() => {
                    handleTabClick(menu)
                  }}
                >
                  <div className="flex flex-row">
                    {/* <span className={`tabs cursor-pointer ${isActive}`}> */}
                    <span
                      className={clsx({
                        flex: true,
                        tabs: true,
                        "text-gray-400": isActive ? false : true,
                        "text-[#000000]": isActive ? true : false,
                        "font-bold": isActive ? true : false,
                        "hover:text-[#000000]": true,
                        "cursor-pointer": true,
                      })}
                    >
                      {menu.name}
                    </span>

                    {menu.isNew ? (
                      <Label className="ml-2 rounded-sm px-2 py-1" textClassName="font-medium text-sm" text="NEW" />
                    ) : null}
                  </div>
                  {selected?.url === menu.url && menu.children && menu.children.length ? (
                    <div className="flex flex-row gap-3">
                      {menu.children.map((child, i) => {
                        if (child.hidden) return null

                        const isActive = child.url.endsWith(location.pathname) ? "active" : null

                        return (
                          <div
                            key={i}
                            className={clsx({
                              flex: true,
                              "pt-3": true,
                              "text-gray-400": isActive ? false : true,
                              "text-[#000000]": isActive ? true : false,
                              "font-bold": isActive ? true : false,
                              "hover:text-[#000000]": true,
                              "animate__animated animate__fadeIn": true,
                              "cursor-pointer": true,
                            })}
                            onClick={() => {
                              handleTabClick(child)
                            }}
                          >
                            <span>{child.name}</span>
                          </div>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
          <div className="flex flex-row gap-3 px-5">
            {accountMenus.map((menu, i) => {
              if (menu.hidden) return null
              const isActive = (
                menu.url !== ROUTES.CAMPAIGN_PAYMENTS_INDEX.PATH &&
                location.pathname == menu.url ? "active" : null
              )

              return (
                <div
                  key={i}
                  className={clsx({
                    flex: true,
                    "pt-3": true,
                    "text-gray-400": isActive ? false : true,
                    "text-[#000000]": isActive ? true : false,
                    "font-bold": isActive ? true : false,
                    "hover:text-[#000000]": true,
                    "cursor-pointer": true,
                    "animate__animated animate__fadeIn": true,
                  })}
                  onClick={() => {
                    handleTabClick(menu)
                  }}
                >
                  <span>{menu.name}</span>
                </div>
              )
            })}
          </div>
          <div className="flex flex-col p-5 gap-8 mt-auto">
            {languageMenus.map((menu, i) => {
              if (menu.hidden) return null

              return (
                <div key={i} className="flex flex-col point-cursor">
                  <div className="flex flex-row">
                    <span
                      className={clsx({
                        flex: true,
                        tabs: true,
                        "text-gray-400": true,
                        "text-[#000000]": true,
                        "font-bold": true,
                        "hover:text-[#000000]": true,
                        "cursor-pointer": true,
                      })}
                    >
                      {menu.name}
                    </span>
                  </div>
                  {menu.children && menu.children.length ? (
                    <div className="flex flex-row gap-3">
                      {menu.children.map((child, i) => {
                        if (child.hidden) return null

                        const isActive = child.locale === props.preferences.locale ? "active" : null

                        return (
                          <div
                            key={i}
                            className={clsx({
                              flex: true,
                              "pt-3": true,
                              "text-gray-400": isActive ? false : true,
                              "text-[#000000]": isActive ? true : false,
                              "font-bold": isActive ? true : false,
                              "hover:text-[#000000]": true,
                              "animate__animated animate__fadeIn": true,
                              "cursor-pointer": true,
                              "flex items-center gap-2": true,
                            })}
                            onClick={() => {
                              handleLanguageClick(child.locale)
                            }}
                          >
                            {child.icon}
                            <span>{child.name}</span>
                          </div>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      </>
    )
  }

  if (!props.sidebar.isVisible || isEmpty(props.auth) || isEmpty(props.brand)) {
    return <></>
  }

  return <>{renderSideBar()}</>
}

export default connect(mapStateToProps, mapDispatchProps)(SidebarContainer)
